import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t } = useTranslation();

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const services = ['footer.s1', 'footer.s2', 'footer.s3', 'footer.s4', 'footer.s5', 'footer.s6'];
  const navLinks = [
    { labelKey: 'footer.nav.about', id: 'about' },
    { labelKey: 'footer.nav.cases', id: 'cases' },
    { labelKey: 'footer.nav.roadmap', id: 'roadmap' },
    { labelKey: 'footer.nav.contact', id: 'contact' },
  ];

  return (
    <footer className="relative bg-[#07010f] overflow-hidden">
      <div className="w-full h-px bg-gradient-to-r from-transparent via-[#00ff41]/60 to-transparent" />
      <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(0,255,65,0.04) 0%, transparent 70%)' }} />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(0,217,255,0.04) 0%, transparent 70%)' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="py-14 border-b border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="flex items-center gap-6">
            <div className="relative w-24 h-24 flex items-center justify-center flex-shrink-0">
              <div className="absolute inset-0 rounded-sm" style={{ background: 'rgba(0,255,65,0.08)', boxShadow: '0 0 28px rgba(0,255,65,0.2)' }} />
              <img src="https://static.readdy.ai/image/792df796680eec38aa20c46d1adacb8b/7b5b4725d7f07737d5bd49d68c5f8cb2.png"
                alt="Pixel Monster" className="w-16 h-16 object-contain relative z-10" />
            </div>
            <div>
              <div className="text-4xl font-black text-white font-['Space_Grotesk'] tracking-tight leading-none">{t('footer.brandName')}</div>
              <div className="text-[#00ff41] font-['JetBrains_Mono'] text-sm tracking-[0.3em] mt-1.5 opacity-80">PIXEL  MONSTER</div>
            </div>
          </div>
          <div className="flex flex-col items-start md:items-end gap-2">
            <p className="text-white/60 font-['Space_Grotesk'] text-sm tracking-widest uppercase">{t('footer.tagline1')}</p>
            <p className="font-['JetBrains_Mono'] text-xs" style={{ color: '#00ff41', textShadow: '0 0 10px #00ff4188' }}>
              {t('footer.tagline2')}
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-10 py-14 border-b border-white/5">
          <div className="md:col-span-4">
            <div className="flex items-center gap-2 mb-6">
              <span className="w-4 h-px bg-[#00ff41]" />
              <span className="text-[#00ff41] font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] font-bold">{t('footer.servicesTitle')}</span>
            </div>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-3">
              {services.map((key, idx) => (
                <li key={idx}>
                  <button onClick={() => scrollToSection('services')}
                    className="group flex items-center gap-2 text-gray-500 hover:text-white transition-colors duration-300 text-sm font-['Space_Grotesk'] cursor-pointer w-full text-left whitespace-nowrap"
                  >
                    <span className="w-1 h-1 rounded-full bg-[#00ff41] flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity duration-300" style={{ boxShadow: '0 0 4px #00ff41' }} />
                    {t(key)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-6">
              <span className="w-4 h-px bg-[#00d9ff]" />
              <span className="text-[#00d9ff] font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] font-bold">{t('footer.navTitle')}</span>
            </div>
            <ul className="space-y-3">
              {navLinks.map((item, idx) => (
                <li key={idx}>
                  <button onClick={() => scrollToSection(item.id)}
                    className="group flex items-center gap-2 text-gray-500 hover:text-white transition-colors duration-300 text-sm font-['Space_Grotesk'] cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-0 group-hover:w-3 h-px bg-[#00d9ff] transition-all duration-300 overflow-hidden" />
                    {t(item.labelKey)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-6">
            <div className="flex items-center gap-2 mb-6">
              <span className="w-4 h-px bg-[#ff006e]" />
              <span className="text-[#ff006e] font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] font-bold">{t('footer.contactTitle')}</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: 'ri-wechat-line', label: t('contact.wechatLabel'), value: 'PixelMonster_AI', color: '#00ff41' },
                { icon: 'ri-mail-line', label: t('contact.emailLabel'), value: 'lilfishegg@gmail.com', color: '#00d9ff' },
                { icon: 'ri-map-pin-line', label: t('footer.locationLabel'), value: t('footer.locationValue'), color: '#ff006e' },
                { icon: 'ri-time-line', label: t('footer.deliveryLabel'), value: t('footer.deliveryValue'), color: '#00ff41' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 rounded-sm bg-white/[0.03] border border-white/[0.05] hover:border-white/10 transition-colors duration-300 group">
                  <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-sm"
                    style={{ background: `${item.color}15`, color: item.color, boxShadow: `0 0 8px ${item.color}30` }}>
                    <i className={`${item.icon} text-base`} />
                  </div>
                  <div>
                    <div className="text-[10px] font-['JetBrains_Mono'] text-gray-600 tracking-widest mb-0.5">{item.label}</div>
                    <div className="text-sm text-gray-300 font-['Space_Grotesk'] group-hover:text-white transition-colors duration-300 whitespace-nowrap">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00ff41]" style={{ boxShadow: '0 0 6px #00ff41', animation: 'footerPulse 2s ease-in-out infinite' }} />
            <p className="text-gray-600 text-xs font-['JetBrains_Mono'] tracking-wider">{t('footer.copyright')}</p>
          </div>
          <p className="text-gray-700 text-xs font-['JetBrains_Mono'] tracking-wider italic">{t('footer.motto')}</p>
          <div className="flex items-center gap-1 text-gray-700 text-xs font-['JetBrains_Mono']">
            <span className="text-[#00ff41] opacity-60">▸</span>
            <span>{t('footer.powered')}</span>
          </div>
        </div>
      </div>

      <div className="w-full h-px bg-gradient-to-r from-transparent via-[#00ff41]/40 to-transparent" />
      <style>{`@keyframes footerPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
    </footer>
  );
}
